/**
 * Client-side image preparation.
 *
 * Phone cameras often produce 4–20 MB images even though the largest
 * image rendered by the app is only a fraction of that size. Preparing
 * the image in the browser saves upload time, mobile data and server CPU.
 * The backend still validates and optimises every upload; this helper is
 * only the first line of defence.
 */

import { FILE_LIMITS } from './tripValidation';

// Hard client-side ceiling. The backend accepts the same maximum for direct
// callers; normal browser uploads are reduced to a high-quality web payload
// before transport.
const MAX_INPUT_BYTES = FILE_LIMITS.maxImageSizeBytes;

const PRESETS = {
  profile: { maxDimension: 1600, maxBytes: 1.2 * 1024 * 1024, quality: 0.88 },
  // 2560 px still exceeds the rendered size on the application's retina
  // layouts, while the tighter byte target makes phone uploads and server
  // thumbnail generation materially faster.
  cover: { maxDimension: 2560, maxBytes: 2 * 1024 * 1024, quality: 0.88 },
  trip: { maxDimension: 2560, maxBytes: 2 * 1024 * 1024, quality: 0.88 },
  default: { maxDimension: 2560, maxBytes: 2 * 1024 * 1024, quality: 0.88 },
};

const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|gif|heic|heif)$/i;
const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
let heicConverterPromise;

const loadHeicConverter = () => {
  if (!heicConverterPromise) {
    heicConverterPromise = import('heic2any').then((module) => module.default || module);
  }
  return heicConverterPromise;
};

export const isHeicImage = (file) => Boolean(
  file && (HEIC_TYPES.includes(String(file.type || '').toLowerCase())
    || /\.(heic|heif)$/i.test(String(file.name || ''))),
);

export const isSupportedImageFile = (file) => Boolean(
  file && (['image/jpeg', 'image/png', 'image/webp', 'image/gif', ...HEIC_TYPES]
    .includes(String(file.type || '').toLowerCase())
    || IMAGE_EXTENSIONS.test(String(file.name || ''))),
);

const loadBitmap = async (file) => {
  // createImageBitmap with EXIF-aware orientation is supported by modern
  // Chromium/Firefox and avoids the portrait/landscape problem caused by
  // mobile cameras storing rotation only in EXIF metadata.
  if (typeof window !== 'undefined' && typeof window.createImageBitmap === 'function') {
    try {
      return await window.createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (_) {
      // Fall through to the <img> path for Safari/older browsers.
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível ler esta imagem.'));
    };
    image.src = url;
  });
};

const canvasToBlob = (canvas, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) resolve(blob);
    else reject(new Error('O navegador não conseguiu preparar a imagem.'));
  }, 'image/jpeg', quality);
});

const jpegName = (name) => {
  const base = String(name || 'imagem').replace(/\.[^./\\]+$/, '');
  return `${base || 'imagem'}.jpg`;
};

/** Convert phone-native HEIC/HEIF photos to a browser/server-compatible JPEG. */
export const convertHeicToJpeg = async (file) => {
  if (!isHeicImage(file)) return file;
  try {
    const heic2any = await loadHeicConverter();
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    if (!blob) throw new Error('A conversão HEIC não devolveu uma imagem.');
    return new File([blob], jpegName(file.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (error) {
    const conversionError = new Error('Não foi possível converter esta foto HEIC. Tenta exportá-la como JPG e seleciona-a novamente.');
    conversionError.cause = error;
    throw conversionError;
  }
};

/**
 * Convert an image to a web-sized JPEG while preserving its aspect ratio.
 * The function returns the original file when it is already small enough
 * and is an ordinary JPEG, avoiding an unnecessary quality loss.
 */
export const optimizeImageForUpload = async (file, preset = 'default') => {
  if (!file) return file;
  const looksLikeImage = file.type?.startsWith('image/') || IMAGE_EXTENSIONS.test(String(file.name || ''));
  if (!looksLikeImage) return file;
  if (!isSupportedImageFile(file)) {
    throw new Error('Formato não suportado. Escolha uma imagem JPG, PNG, WEBP ou HEIC.');
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('A imagem excede o limite de 20 MB. Escolha uma imagem mais pequena.');
  }

  const sourceFile = await convertHeicToJpeg(file);
  const options = PRESETS[preset] || PRESETS.default;
  const bitmap = await loadBitmap(sourceFile);
  const sourceWidth = bitmap.width;
  const sourceHeight = bitmap.height;
  const scale = Math.min(1, options.maxDimension / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  if (sourceFile.type === 'image/jpeg' && sourceFile.size <= options.maxBytes && scale === 1) {
    bitmap.close?.();
    return sourceFile;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) {
    bitmap.close?.();
    throw new Error('O navegador não conseguiu preparar a imagem.');
  }

  // White background avoids black transparent areas when a PNG is converted
  // to JPEG. Photos do not need to retain an alpha channel.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  // Lower quality gradually only when needed. This keeps ordinary photos
  // sharp while guaranteeing a bounded payload for the common presets.
  let quality = options.quality;
  let blob = await canvasToBlob(canvas, quality);
  for (let i = 0; i < 4 && blob.size > options.maxBytes; i += 1) {
    quality -= 0.06;
    blob = await canvasToBlob(canvas, Math.max(0.72, quality));
  }

  // If conversion somehow produces a larger file and no resize was needed,
  // retain the original JPEG rather than making it worse.
  if (sourceFile.type === 'image/jpeg' && scale === 1 && blob.size >= sourceFile.size) {
    return sourceFile;
  }

  return new File([blob], jpegName(file.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

export const IMAGE_UPLOAD_LIMITS = {
  maxInputBytes: MAX_INPUT_BYTES,
  acceptedTypes: 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif',
};
